import type React from "react";

import { ROLES } from "@infinitaslearning/module-he-common";
import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import { AccessibilityMenuModal } from "components/AccessibilityMenuModal/AccessibilityMenuModal";
import { SideMenuModal } from "components/SideMenuModal";
import { UsetifulTour } from "constants/hst-constants";
import { modalIds } from "constants/modals-constants";
import {
  GENERIC_ROUTES,
  PRODUCT_PAGES_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEACHER_SCHEDULED_TEST_PAGE_ROUTES,
} from "constants/routes";
import { useCurrentPathMatch } from "hooks/useCurrentPathMatch";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useModal } from "hooks/useModal";
import { useProfile } from "hooks/useProfile";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import { useUsetiful } from "hooks/useUsetiful";
import useRequest from "hooks/user/useRequest";
import { RoleComponent } from "layouts/RoleComponent";
import { InsightProductSelector } from "pages/TeacherEnvironment/components/InsightProductSelector/InsightProductSelector";
import type { ROLE } from "persistence";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { CssFlex, CssIcon } from "styles/helpers/layout";
import { NavigationMenuItem } from "../NavigationMenuItem/NavigationMenuItem";
import type { NavigationMenuProps } from "./NavigationMenu.model";
import {
  StyledAccessibilityMenuButton,
  StyledButton,
  StyledIcon,
  StyledLogo,
  StyledLogoContainer,
  StyledNavigationBottomContainer,
  StyledNavigationContainer,
  StyledNavigationItemGroup,
  StyledNavigationMenu,
  StyledProfileButton,
  StyledProfileIcon,
} from "./NavigationMenu.styled";

// Pages where the side menu should not appear
const routesWithoutSideMenu: string[] = [
  TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_RESULTS_FOR_STUDENT,
];

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  className,
  dataCy,
  menuItems,
  showGoToStudentEnvButton = false,
  showGoToTeacherEnvButton = false,
}) => {
  const { isBusinessDomain, getCustomLogoByDomain, getStudentTermByDomain } = useDomainHandler();
  const { t } = useTranslation("homepage");
  const selectedProductId = useSelectedProductId();
  const { currentRouteMatchesAny } = useCurrentPathMatch();
  const { toggleModal, isModalOpen } = useModal();
  const { isUsetifulTourAPIAvailable, openTour } = useUsetiful();
  const { userDetails, updateUserDetails } = useProfile();
  const { logout } = useRequest();
  const navigate = useNavigate();
  const shouldShowSideNav = !currentRouteMatchesAny(routesWithoutSideMenu);
  const onMenuButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    toggleModal(modalIds.modalSideMenu);
  };

  const onCloseSideMenu = () => {
    if (isModalOpen(modalIds.modalSideMenu)) {
      toggleModal(modalIds.modalSideMenu);
    }
  };

  const handleOnboardingButtonClick = () => {
    trackPlatformEvent({
      module: STUDENT_MODULES.DASHBOARD,
      itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_BEGIN_ONBOARDING,
      value: JSON.stringify({
        isBusinessDomain: isBusinessDomain(),
      }),
    });
    openTour(
      isBusinessDomain()
        ? UsetifulTour.BUSINESS_STUDENT_ONBOARDING
        : UsetifulTour.REGULAR_STUDENT_ONBOARDING,
    );
  };

  // Onboarding button should only show in some specific routes
  const showOnboardingButton =
    isUsetifulTourAPIAvailable() &&
    currentRouteMatchesAny([
      PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS,
      PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS,
    ]);

  const navigateToStudentEnvironment = () => {
    if (selectedProductId) {
      return navigate(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
          productId: selectedProductId,
        }),
      );
    }
  };

  return (
    <aside>
      <StyledNavigationContainer
        gridTemplateColumns="1fr"
        gridTemplateRows={!isBusinessDomain() ? "auto 1fr auto" : "1fr auto"}
      >
        {!isBusinessDomain() && (
          <StyledLogoContainer>
            <Link aria-label={t("header.button.goToMainPage")} to={GENERIC_ROUTES.HOME_PAGE}>
              <StyledLogo dataCy="sidebar-app-logo" iconName={getCustomLogoByDomain(true)} />
            </Link>
          </StyledLogoContainer>
        )}
        <StyledNavigationMenu className={className} data-cy={dataCy}>
          {menuItems.map((menuItem) => {
            if (menuItem.redirectPath) {
              return (
                <NavigationMenuItem
                  key={menuItem.id}
                  dataCy={menuItem.dataCy}
                  eventTrackData={menuItem.eventTrackData}
                  excludeFromActivePath={menuItem.excludeFromActivePath}
                  nestingLevel={menuItem.nestingLevel}
                  onlyActiveOnExactPath={menuItem.onlyActiveOnExactPath}
                  redirectPath={menuItem.redirectPath}
                >
                  {menuItem.title}
                </NavigationMenuItem>
              );
            }
            if (menuItem.subItems && menuItem.subItems.length > 0) {
              return (
                <StyledNavigationItemGroup
                  key={menuItem.id}
                  basic
                  dataCy={menuItem.dataCy}
                  title={menuItem.title as string}
                  hideArrowIcon={true}
                  expandedByDefault={menuItem.expandedByDefault}
                  isExpanded={menuItem.isExpanded}
                  onToggle={menuItem.onToggle}
                >
                  {menuItem.subItems?.map((subItem) => (
                    <NavigationMenuItem
                      key={subItem.id}
                      dataCy={subItem.dataCy}
                      eventTrackData={subItem.eventTrackData}
                      excludeFromActivePath={subItem.excludeFromActivePath}
                      nestingLevel={subItem.nestingLevel}
                      redirectPath={subItem.redirectPath}
                    >
                      {subItem.title}
                    </NavigationMenuItem>
                  ))}
                </StyledNavigationItemGroup>
              );
            }
            // no redirect path and also no sub items, don't show this item
            return null;
          })}
        </StyledNavigationMenu>
        <StyledNavigationBottomContainer>
          {selectedProductId && (
            <RoleComponent allowedRoles={[ROLES.STUDENT as ROLE, ROLES.TEACHER as ROLE]}>
              <InsightProductSelector selectedProductId={selectedProductId} />
            </RoleComponent>
          )}
          <StyledAccessibilityMenuButton variant="side-menu" />
          {showOnboardingButton && (
            <StyledButton
              dataCy="open-onboarding-tour-button"
              variant="default"
              onClick={handleOnboardingButtonClick}
            >
              <StyledIcon iconName="far fa-question-circle" iconSize="lg" />
              {t("header.openOnboarding")}
            </StyledButton>
          )}
          <RoleComponent allowedRoles={[ROLES.TEACHER as ROLE]}>
            <>
              {showGoToTeacherEnvButton && (
                <StyledButton
                  dataCy="teacher-go-to-teacher-env-button"
                  variant="default"
                  onClick={() => navigate(TEACHER_HOME_PAGE_ROUTES.HOME_PAGE)}
                >
                  <StyledIcon iconName="fas fa-chalkboard-teacher" iconSize="lg" />
                  {t("header.toTeacherEnvButton.title", {
                    studentTerm: getStudentTermByDomain(),
                  })}
                </StyledButton>
              )}
              {showGoToStudentEnvButton && (
                <StyledButton
                  dataCy="teacher-go-to-student-env-button"
                  variant="default"
                  onClick={navigateToStudentEnvironment}
                >
                  <StyledIcon iconName="far fa-user-graduate" iconSize="lg" />
                  {t("header.toStudentEnvButton.title", "To practice environment")}
                </StyledButton>
              )}
            </>
          </RoleComponent>
          {shouldShowSideNav && (
            <StyledProfileButton
              alignItems="center"
              data-cy="open-side-menu-button"
              gap={1}
              onClick={onMenuButtonClick}
            >
              <StyledProfileIcon>
                <CssIcon iconName="fas fa-user" iconSize="lg" />
              </StyledProfileIcon>
              <CssFlex flexDirection="column">
                <ParagraphLarge data-cy="account-header-name">{`${userDetails?.firstName} ${userDetails?.lastName}`}</ParagraphLarge>
                <ParagraphMedium>{userDetails?.schoolNumber}</ParagraphMedium>
              </CssFlex>
            </StyledProfileButton>
          )}
        </StyledNavigationBottomContainer>
      </StyledNavigationContainer>
      {/* -- Modals (must be out of fixed nav container to avoid z-index issues) -- */}
      {shouldShowSideNav && (
        <>
          <SideMenuModal
            userDetails={userDetails}
            onClose={onCloseSideMenu}
            onLogout={logout}
            onSubmit={updateUserDetails}
          />
          <AccessibilityMenuModal onClose={onCloseSideMenu} />
        </>
      )}
    </aside>
  );
};
